import Cookies from 'js-cookie';

import { isDevEnvironmentSwitcherEnabled } from './environments';

const COOKIE_KEY = 'DEV_EMBED_SANDBOX';
const cookieOptions = { path: '/' };

export const EMBED_SANDBOX_COOKIE_KEY = COOKIE_KEY;
export const EMBED_SANDBOX_ENTITY_KEY = 'salesOrders';
/** Dev-only standalone server (`pnpm --filter @Sage/ssm-embed start:example`). */
export const EMBED_SANDBOX_PORT = 3016;
export const EMBED_SANDBOX_PATH = '/ssm-embed/example.html';
export const EMBED_SANDBOX_STANDALONE_URL = `https://localhost:${EMBED_SANDBOX_PORT}/example.html`;

const HOST_EMBED_EXAMPLE_URL = {
    'app-pre.forcemanager.net': `https://app-pre.forcemanager.net${EMBED_SANDBOX_PATH}`,
    'app.forcemanager.net': `https://app.forcemanager.net${EMBED_SANDBOX_PATH}`,
};

const isLocalHost = (win = window) => {
    const hostname = win?.location?.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
};

const formatLocalHost = (hostname) => (hostname.includes(':') ? `[${hostname}]` : hostname);

/** Cookie is only honored where Dev Tools is enabled (local dev, staging, pre). */
export const isEmbedSandboxAvailable = () => isDevEnvironmentSwitcherEnabled();

export const isEmbedSandboxEnabled = (win = window) => {
    if (!isEmbedSandboxAvailable(win)) return false;
    return Cookies.get(COOKIE_KEY) === '1';
};

export const setEmbedSandboxEnabled = (enabled, win = window) => {
    if (!isEmbedSandboxAvailable(win)) return;

    if (enabled) {
        Cookies.set(COOKIE_KEY, '1', cookieOptions);
        return;
    }

    Cookies.remove(COOKIE_KEY, cookieOptions);
};

export const getEmbedSandboxIframeUrl = (win = window) => {
    if (isLocalHost(win)) {
        const { origin } = win.location;
        if (origin && origin !== 'null') {
            return `${origin}${EMBED_SANDBOX_PATH}`;
        }
    }

    const hostname = win?.location?.hostname;
    if (hostname && HOST_EMBED_EXAMPLE_URL[hostname]) {
        return HOST_EMBED_EXAMPLE_URL[hostname];
    }

    return EMBED_SANDBOX_STANDALONE_URL;
};

export const getEmbedSandboxUnavailableText = (
    url = EMBED_SANDBOX_STANDALONE_URL,
    win = window,
) => {
    if (isLocalHost(win)) {
        return `Could not load ${url}. Restart pnpm start:web4 — the example page is served at ${EMBED_SANDBOX_PATH} during local dev.`;
    }

    if (win?.location?.hostname && HOST_EMBED_EXAMPLE_URL[win.location.hostname]) {
        return `Could not load ${url}. Check that ${EMBED_SANDBOX_PATH} is deployed on this host.`;
    }

    return `Could not load ${url}. Start pnpm --filter @Sage/ssm-embed start:example or use Web4 on localhost / app-pre where the example is served without a separate port.`;
};

export const probeEmbedSandboxUrl = (url, fetchImpl = fetch) => {
    if (!url) return Promise.resolve(false);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);

    return fetchImpl(url, {
        cache: 'no-store',
        mode: 'no-cors',
        signal: controller.signal,
    })
        .then(
            () => true,
            () => false,
        )
        .finally(() => {
            clearTimeout(timer);
        });
};

export const getEmbedSandboxConfig = (entityKey, win = window) => {
    if (!isEmbedSandboxEnabled(win) || entityKey !== EMBED_SANDBOX_ENTITY_KEY) return null;

    return {
        actions: ['create', 'edit'],
        id: 0,
        sandbox: true,
        url: getEmbedSandboxIframeUrl(win),
    };
};
